import Header from '../../components/public/Header';
import Footer from '../../components/public/Footer';
import { Link } from 'react-router-dom';
import { Eye, Phone, Mail, ArrowRight, ShieldAlert } from 'lucide-react';

const Section = ({ title, children }) => (
  <div>
    <h2 className="font-heading text-xl font-semibold text-text-primary mb-3">{title}</h2>
    <div className="text-text-muted leading-relaxed space-y-3">{children}</div>
    <hr className="border-[#E4E8EB] mt-6" />
  </div>
);

export default function WhistleblowingPage() {
  return (
    <div className="min-h-screen bg-[#F8FAFA]">
      <Header />

      {/* Hero */}
      <section className="bg-white py-16 lg:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-16 h-16 bg-accent rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Eye className="h-8 w-8 text-primary" />
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-text-primary mb-4 leading-tight">
            Speak Up — Whistleblowing Policy
          </h1>
          <p className="text-lg text-text-muted leading-relaxed">
            Osabea Healthcare Solutions encourages anyone with genuine concerns about wrongdoing to speak up, without fear of reprisal.
          </p>
          <p className="text-sm text-text-muted mt-4">Last updated: June 2026</p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl border border-[#E4E8EB] p-8 space-y-6">

            <Section title="What is whistleblowing?">
              <p>
                Whistleblowing (also called "making a protected disclosure") is when a worker, volunteer, or contractor raises a concern in the public interest about wrongdoing, risk, or malpractice that they have witnessed in the course of their work.
              </p>
              <p>
                This policy is in place in accordance with the <strong>Public Interest Disclosure Act 1998 (PIDA)</strong>, as amended by the Enterprise and Regulatory Reform Act 2013, and CQC guidance on openness and transparency.
              </p>
            </Section>

            <Section title="What concerns can be raised?">
              <p>You are encouraged to speak up if you believe (in good faith) that there is evidence of:</p>
              <ul className="list-disc list-inside space-y-1 mt-1">
                <li>A criminal offence (e.g. fraud, financial irregularities)</li>
                <li>Abuse, neglect, or mistreatment of a service user</li>
                <li>A health and safety risk to any person</li>
                <li>A breach of a legal or regulatory obligation</li>
                <li>Miscarriage of justice</li>
                <li>Deliberate concealment of information about any of the above</li>
                <li>Any act or omission that puts a vulnerable person at risk of harm</li>
              </ul>
              <p>
                This policy is for concerns in the <em>public interest</em>, not personal grievances. If you have a personal grievance (e.g. a dispute with a colleague or manager), please use our Grievance Procedure instead.
              </p>
            </Section>

            <Section title="Your protection">
              <p>
                Osabea Healthcare Solutions is committed to ensuring that anyone who raises a genuine concern under this policy is <strong>protected from any detriment, victimisation, or dismissal</strong> as a result.
              </p>
              <p>
                Under the Public Interest Disclosure Act 1998, workers who make a protected disclosure are legally protected from dismissal or detriment. We take any form of retaliation against a whistleblower extremely seriously and will treat it as a disciplinary matter.
              </p>
              <p>
                You may raise a concern anonymously, although this may limit our ability to investigate fully and provide feedback to you.
              </p>
            </Section>

            <Section title="How to raise a concern internally">
              <p>In the first instance, please raise your concern with your line manager or with our Registered Manager. If the concern relates to your line manager or the Registered Manager, please contact the Director directly:</p>
              <ul className="list-disc list-inside space-y-1 mt-2">
                <li><strong>By telephone:</strong> <a href="tel:01622809469" className="text-primary underline">01622 809469</a></li>
                <li><strong>By email:</strong> <a href="mailto:info@osabeacares.co.uk" className="text-primary underline">info@osabeacares.co.uk</a></li>
                <li><strong>In writing:</strong> Osabea Healthcare Solutions Limited, Suite FA4D, 1 St Faith's Street, Maidstone, Kent, ME14 1LH</li>
              </ul>
              <p>
                We will acknowledge your concern within <strong>5 working days</strong> and will keep you informed of any investigation and outcomes, to the extent permitted by law and confidentiality obligations.
              </p>
            </Section>

            <Section title="External reporting routes">
              <p>If you do not feel comfortable raising your concern internally, or if the concern involves senior management, you may report directly to an external prescribed body. These include:</p>
              <ul className="list-disc list-inside space-y-2 mt-2">
                <li>
                  <strong>Care Quality Commission (CQC)</strong> — for concerns about the quality or safety of regulated care activities:<br />
                  <a href="https://www.cqc.org.uk/give-feedback-on-care" target="_blank" rel="noopener noreferrer" className="text-primary underline">cqc.org.uk</a> | 03000 616161
                </li>
                <li>
                  <strong>Health and Safety Executive (HSE)</strong> — for concerns about health and safety risks:<br />
                  <a href="https://www.hse.gov.uk/contact/concerns.htm" target="_blank" rel="noopener noreferrer" className="text-primary underline">hse.gov.uk</a> | 0300 003 1647
                </li>
                <li>
                  <strong>Local Authority Designated Officer (LADO)</strong> — for concerns about a person in a position of trust who may have harmed or could harm a child or vulnerable adult:<br />
                  Contact Kent County Council Safeguarding Team: 03000 411111
                </li>
                <li>
                  <strong>Disclosure and Barring Service (DBS)</strong> — to report a person who should be barred from working with vulnerable groups:<br />
                  <a href="https://www.gov.uk/government/organisations/disclosure-and-barring-service" target="_blank" rel="noopener noreferrer" className="text-primary underline">gov.uk/dbs</a>
                </li>
                <li>
                  <strong>HMRC</strong> — for concerns about fraud or tax irregularities:<br />
                  <a href="https://www.gov.uk/report-an-employee-to-hmrc" target="_blank" rel="noopener noreferrer" className="text-primary underline">gov.uk/report-fraud-hmrc</a>
                </li>
              </ul>
              <p>
                You can also contact <strong>Protect</strong> (formerly Public Concern at Work), the UK whistleblowing charity, for free confidential advice: <a href="https://protect-advice.org.uk" target="_blank" rel="noopener noreferrer" className="text-primary underline">protect-advice.org.uk</a> | 020 3117 2520.
              </p>
            </Section>

            <Section title="Confidentiality">
              <p>
                We will treat all concerns raised under this policy with sensitivity and confidentiality. Where possible, we will not disclose your identity to those implicated in the concern without your consent, unless we are legally required to do so.
              </p>
            </Section>

            <Section title="False allegations">
              <p>
                This policy is intended for genuine, good-faith concerns. If you make an allegation that you know to be untrue or malicious, this may result in disciplinary action against you.
              </p>
            </Section>

          </div>

          {/* Contact */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-8 mt-6">
            <div className="flex items-start gap-4">
              <ShieldAlert className="h-6 w-6 text-amber-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-heading font-semibold text-amber-900 mb-2">Have an urgent safeguarding concern?</h3>
                <p className="text-amber-800 text-sm mb-4">
                  If you believe a person is in immediate danger, call <strong>999</strong> immediately. For non-emergency safeguarding concerns, contact our Designated Safeguarding Lead or the CQC.
                </p>
                <Link to="/safeguarding" className="inline-flex items-center gap-2 text-sm text-amber-700 font-semibold hover:underline">
                  Read our Safeguarding Policy <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
            </div>
          </div>

        </div>
      </section>

      <Footer />
    </div>
  );
}
